import React, { useState } from "react";
import {
  Phone,
  Mail,
  Send,
  Clock,
  Copy,
  Check,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  User,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatPhoneInput, handlePhoneInputChange, handlePhoneKeyDown, getPublicAppBaseUrl, cn } from "@/lib/utils";
import AuthService from "@/lib/auth";

interface SendLeadRegistrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinkSentSuccess?: () => void;
}

export default function SendLeadRegistrationModal({
  open,
  onOpenChange,
  onLinkSentSuccess,
}: SendLeadRegistrationModalProps) {
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"sms" | "email">("sms");
  const [errors, setErrors] = useState<{
    firstName?: string;
    middleName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  }>({});
  const [isSending, setIsSending] = useState(false);
  const [generatedLinkData, setGeneratedLinkData] = useState<{
    id: string;
    token: string;
    registration_url: string;
    expires_at: string;
    sms_sent: boolean;
    email_sent: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const sendSms = deliveryMethod === "sms";
  const sendEmail = deliveryMethod === "email";

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePhoneInputChange(e, (formatted) => {
      setPhone(formatted);
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
    }, formatPhoneInput);
  };

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: typeof errors = {};
    const NAME_REGEX = /^[A-Za-z\s'-]+$/;

    // Validate First Name (required, alphabet/hyphen/apostrophe only)
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!NAME_REGEX.test(firstName.trim())) {
      newErrors.firstName = "First name should not contain numbers or special characters";
    }

    // Validate Middle Name (optional, alphabet/hyphen/apostrophe only if present)
    if (middleName.trim() && !NAME_REGEX.test(middleName.trim())) {
      newErrors.middleName = "Middle name should not contain numbers or special characters";
    }

    // Validate Last Name (required, alphabet/hyphen/apostrophe only)
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!NAME_REGEX.test(lastName.trim())) {
      newErrors.lastName = "Last name should not contain numbers or special characters";
    }

    let digits = phone.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }

    if (sendSms && digits.length !== 10) {
      newErrors.phone = "Mobile number must be exactly 10 digits";
    }

    if (sendEmail && (!email || !email.includes("@"))) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      setIsSending(true);
      const res: any = await AuthService.generateRegistrationLink({
        first_name: firstName.trim(),
        middle_name: middleName.trim() || undefined,
        last_name: lastName.trim(),
        phone: digits ? `+1${digits}` : undefined,
        email: email.trim() || undefined,
        send_sms: sendSms,
        send_email: sendEmail,
        base_url: getPublicAppBaseUrl(),
      });

      if (res?.data || res?.registration_url) {
        const linkData = { ...(res.data || res) };
        if (
          linkData.registration_url &&
          (linkData.registration_url.includes("localhost") ||
            linkData.registration_url.includes("127.0.0.1"))
        ) {
          linkData.registration_url = linkData.registration_url.replace(
            /^http:\/\/[^/]+/,
            getPublicAppBaseUrl()
          );
        }
        setGeneratedLinkData(linkData);
        toast.success("10-Minute Registration link generated and sent!");
        if (onLinkSentSuccess) onLinkSentSuccess();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to generate registration link");
    } finally {
      setIsSending(false);
    }
  };

  const handleCopy = () => {
    if (!generatedLinkData?.registration_url) return;
    navigator.clipboard.writeText(generatedLinkData.registration_url);
    setCopied(true);
    toast.success("Registration link copied to clipboard");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleResetForNew = () => {
    setGeneratedLinkData(null);
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setDeliveryMethod("sms");
    setErrors({});
    setCopied(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl p-6 sm:p-8 bg-white border border-gray-200 shadow-2xl">
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-green-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-[#1a7a4a]" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Phone Call &bull; Send Lead Registration Link
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                Send a 10-minute self-registration link while on the call with the lead.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {generatedLinkData ? (
          /* Success Screen with Link Details */
          <div className="space-y-5 pt-2">
            <div className="bg-[#f0fdf4] border border-green-200 rounded-2xl p-5 space-y-3">
              <div className="flex items-center gap-2 text-green-800 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span>Link Dispatched to Lead</span>
              </div>
              <p className="text-xs text-green-700 leading-relaxed">
                The lead now has access to the basic registration form. Once they submit, their data will appear in the <strong>Registration Queue</strong> tab.
              </p>

              <div className="flex flex-wrap gap-2 pt-1 text-xs">
                {generatedLinkData.sms_sent && (
                  <span className="bg-green-200/60 text-green-800 px-2 py-0.5 rounded-md font-medium">
                    ✓ SMS Delivered
                  </span>
                )}
                {generatedLinkData.email_sent && (
                  <span className="bg-green-200/60 text-green-800 px-2 py-0.5 rounded-md font-medium">
                    ✓ Email Delivered
                  </span>
                )}
                <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Valid for 10 minutes
                </span>
              </div>
            </div>

            {/* Link Copy Box */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">
                Direct Registration URL
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={generatedLinkData.registration_url}
                  className="h-11 bg-gray-50 text-xs font-mono rounded-xl truncate"
                />
                <Button
                  type="button"
                  onClick={handleCopy}
                  className="h-11 px-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs flex-shrink-0 flex items-center gap-1.5"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* On-Call Resend Tip */}
            <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-2xl text-xs text-blue-800 space-y-1">
              <span className="font-semibold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                Phone Call Re-edit Assistance:
              </span>
              <p className="text-[11px] text-blue-700 leading-relaxed">
                If the lead messes up any details or the link expires before they submit, simply click &quot;Generate New Link&quot; below to send a fresh link instantly.
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2">
              <Button
                variant="outline"
                type="button"
                onClick={handleResetForNew}
                className="rounded-xl border-gray-200 text-gray-700 text-xs h-10"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Generate Another Link
              </Button>

              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl text-xs font-semibold h-10 px-5 shadow-sm"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          /* Form Input Screen */
          <form onSubmit={handleSendLink} className="space-y-4 pt-1">
            {/* First Name (Row 1: Spans alone at top) */}
            <div className="space-y-1.5">
              <Label htmlFor="lead_first_name" className="text-xs font-semibold text-gray-700">
                First Name <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="lead_first_name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
                  }}
                  className={cn(
                    "pl-10 h-11 rounded-xl bg-gray-50 border-gray-200 text-sm",
                    errors.firstName && "border-red-500 bg-red-50"
                  )}
                />
              </div>
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1 font-medium">{errors.firstName}</p>
              )}
            </div>

            {/* Row 2: Middle Name & Last Name spanning 1 row together */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-5 space-y-1.5">
                <Label htmlFor="lead_middle_name" className="text-xs font-semibold text-gray-700">
                  Middle Name <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <Input
                  id="lead_middle_name"
                  placeholder="Middle"
                  value={middleName}
                  onChange={(e) => {
                    setMiddleName(e.target.value);
                    if (errors.middleName) setErrors((prev) => ({ ...prev, middleName: undefined }));
                  }}
                  className={cn(
                    "h-11 rounded-xl bg-gray-50 border-gray-200 text-sm",
                    errors.middleName && "border-red-500 bg-red-50"
                  )}
                />
                {errors.middleName && (
                  <p className="text-xs text-red-500 mt-1 font-medium">{errors.middleName}</p>
                )}
              </div>

              <div className="col-span-7 space-y-1.5">
                <Label htmlFor="lead_last_name" className="text-xs font-semibold text-gray-700">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="lead_last_name"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
                    }}
                    className={cn(
                      "pl-10 h-11 rounded-xl bg-gray-50 border-gray-200 text-sm",
                      errors.lastName && "border-red-500 bg-red-50"
                    )}
                  />
                </div>
                {errors.lastName && (
                  <p className="text-xs text-red-500 mt-1 font-medium">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Mobile Number */}
            <div className="space-y-1.5">
              <Label htmlFor="lead_phone" className="text-xs font-semibold text-gray-700">
                Mobile Number {sendSms && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="lead_phone"
                  placeholder="(555) 000-0000"
                  maxLength={14}
                  value={phone}
                  onChange={handlePhoneChange}
                  onKeyDown={handlePhoneKeyDown}
                  className={cn(
                    "pl-10 h-11 rounded-xl bg-gray-50 border-gray-200 text-sm",
                    errors.phone && "border-red-500 bg-red-50"
                  )}
                />
              </div>
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1 font-medium">{errors.phone}</p>
              )}
            </div>

            {/* Email Address */}
            <div className="space-y-1.5">
              <Label htmlFor="lead_email" className="text-xs font-semibold text-gray-700">
                Email Address {sendEmail && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="lead_email"
                  type="email"
                  placeholder="lead@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  className={cn(
                    "pl-10 h-11 rounded-xl bg-gray-50 border-gray-200 text-sm",
                    errors.email && "border-red-500 bg-red-50"
                  )}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 mt-1 font-medium">{errors.email}</p>
              )}
            </div>

            {/* Delivery Method (Either SMS or Email, not both) */}
            <div className="pt-2 space-y-2">
              <Label className="text-xs font-semibold text-gray-700">
                Delivery Method <span className="text-gray-400 font-normal">(Select Phone or Email)</span>
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDeliveryMethod("sms")}
                  className={`p-3 rounded-xl border cursor-pointer flex items-center gap-2.5 transition-all text-left select-none ${
                    deliveryMethod === "sms"
                      ? "bg-green-50 border-[#1a7a4a] ring-1 ring-[#1a7a4a] text-green-950 font-semibold shadow-xs"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                      deliveryMethod === "sms"
                        ? "border-[#1a7a4a] bg-[#1a7a4a]"
                        : "border-gray-400 bg-white"
                    }`}
                  >
                    {deliveryMethod === "sms" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Phone className="w-3.5 h-3.5 text-[#1a7a4a] flex-shrink-0" />
                    <span className="text-xs truncate">Send via SMS</span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryMethod("email")}
                  className={`p-3 rounded-xl border cursor-pointer flex items-center gap-2.5 transition-all text-left select-none ${
                    deliveryMethod === "email"
                      ? "bg-green-50 border-[#1a7a4a] ring-1 ring-[#1a7a4a] text-green-950 font-semibold shadow-xs"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                      deliveryMethod === "email"
                        ? "border-[#1a7a4a] bg-[#1a7a4a]"
                        : "border-gray-400 bg-white"
                    }`}
                  >
                    {deliveryMethod === "email" && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-[#1a7a4a] flex-shrink-0" />
                    <span className="text-xs truncate">Send via Email</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-xl border-gray-200 text-gray-600 h-11 text-xs"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSending}
                className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl h-11 px-5 font-semibold text-xs shadow-md flex items-center gap-2"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending Link...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Registration Link</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
